import { VSCodeBrowserView } from "@/components/ai/chat/AgentExecutionConversation";
import type { EnvironmentViewMode } from "@/components/ai/ViewModeToggle";
import { EnvironmentBanner } from "@/components/environments/EnvironmentBanner";
import { EnvironmentDetailsTopHeader } from "@/components/environments/EnvironmentDetailsTopHeader";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/flexkit/ResizablePanel";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { GlobalOverloadBanner } from "@/components/nudges/GlobalOverloadBanner";
import { cn } from "@/components/podkit/lib/cn";
import { MainContent } from "@/components/SidebarLayout";

import { VSCodeIpcProvider } from "@/contexts/VSCodeIpcContext";
import { useDynamicTitle } from "@/hooks/use-dynamic-title";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useEnvironmentConversation, getAgentExecution } from "@/hooks/use-environment-conversation";
import type { EnvironmentDetailsOutletContext } from "@/hooks/use-environment-details-outlet-context";
import { useEnvironmentViewMode } from "@/hooks/use-environment-view-mode";
import {
    useFlagOnaAIEnabled,
    useFlagOnaEnvironmentOnboardingEnabled,
    useFlagIsMobileVscodeEnabled,
    useFlagReviewCommentsEnabled,
    useSystemPortsExcludedFromPolicy,
} from "@/hooks/use-flag";
import { useFollowMode, type FollowModeState } from "@/hooks/use-follow-mode";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { useEditorURLQuery } from "@/queries/editor-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import { useRunner } from "@/queries/runner-queries";
import { EnvironmentAgentExecutionConversation } from "@/routes/environments/details/EnvironmentAgentExecutionConversation";
import {
    EnvironmentChangesView,
    type EnvironmentChangesViewRef,
} from "@/routes/environments/details/EnvironmentChangesView";

import { hasLLMCapabilities } from "@/utils/runner-capabilities";

import type { VSCodeIPC } from "@/vscode-ipc";
import type React from "react";
import { Suspense, useCallback, useEffect, useId, useMemo, useRef, useState, type FC } from "react";
import type { ImperativePanelGroupHandle, ImperativePanelHandle } from "react-resizable-panels";
import { getPanelElement, getPanelGroupElement } from "react-resizable-panels";
import { Outlet, useParams } from "react-router-dom";

export const EnvironmentDetailsLayout: FC = () => {
    useDynamicTitle("Environment");

    const { environmentId = "" } = useParams();
    const { data: environment } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);
    const scope = useMemo(() => ({ case: "environmentId" as const, value: environmentId }), [environmentId]);
    useStreamEvents(scope);
    const { environmentConversation } = useEnvironmentConversation(environmentId);
    const { value: isOnaEnvironmentOnboardingEnabled } = useFlagOnaEnvironmentOnboardingEnabled();
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();

    const isFeatureFlagEnabled = isOnaEnabled || isOnaEnvironmentOnboardingEnabled;
    const runnerSupportsLLM = hasLLMCapabilities(runner);

    const shouldShowConversationView = isFeatureFlagEnabled && runnerSupportsLLM;

    if (shouldShowConversationView) {
        return (
            <VSCodeIpcProvider>
                <GlobalOverloadBanner />
                <EnvironmentDetailsWithConversation
                    environmentConversation={environmentConversation}
                    environmentId={environmentId}
                />
            </VSCodeIpcProvider>
        );
    }

    // Show regular details page when feature flags are disabled OR runner lacks LLM capabilities
    return (
        <>
            <GlobalOverloadBanner />
            <MainContent>
                <Suspense>
                    <VSCodeIpcProvider>
                        <EnvironmentDetailsLayoutOutlet context={{ showingAgentConversation: false }} />
                    </VSCodeIpcProvider>
                </Suspense>
            </MainContent>
        </>
    );
};

type EnvironmentAgentExecutionConversationPanelProps = {
    environmentConversation: ReturnType<typeof useEnvironmentConversation>["environmentConversation"];
    environmentId: string;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    followMode?: FollowModeState;
    onBuildButtonAppear?: (specFilePath: string | null) => void;
};

const EnvironmentAgentExecutionConversationPanel: FC<EnvironmentAgentExecutionConversationPanelProps> = ({
    environmentConversation,
    environmentId,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    followMode,
    onBuildButtonAppear,
}) => {
    const agentExecution = getAgentExecution(environmentConversation);

    return (
        <EnvironmentAgentExecutionConversation
            key={agentExecution?.id || `empty-${environmentId}`}
            agentExecution={agentExecution}
            isDetailsPanelOpen={isDetailsPanelOpen}
            onToggleDetailsPanel={onToggleDetailsPanel}
            followMode={followMode}
            environmentId={environmentId}
            onBuildButtonAppear={onBuildButtonAppear}
        />
    );
};

const EnvironmentDetailsPanel: FC<{
    environmentId: string;
    showConversationPanel: boolean;
    onToggleDetailsPanel?: () => void;
    onToggleConversationPanel?: () => void;
    isConversationPanelOpen?: boolean;
    onViewModeChange?: (mode: EnvironmentViewMode) => void;
    followMode?: ReturnType<typeof useFollowMode>;
    onCurrentViewMode?: (mode: EnvironmentViewMode) => void;
    changesViewRef?: React.RefObject<EnvironmentChangesViewRef>;
    /** File path queued for preview — consumed by CodeChangesPanel on mount */
    pendingSpecPreview?: string | null;
    onPendingSpecPreviewConsumed?: () => void;
    /** Parent-driven view mode change (e.g. Build button switching to changes tab) */
    requestedViewMode?: EnvironmentViewMode | null;
    onRequestedViewModeConsumed?: () => void;
}> = ({
    environmentId,
    showConversationPanel,
    onToggleDetailsPanel,
    onToggleConversationPanel,
    isConversationPanelOpen = true,
    onViewModeChange,
    followMode,
    onCurrentViewMode,
    changesViewRef,
    pendingSpecPreview,
    onPendingSpecPreviewConsumed,
    requestedViewMode,
    onRequestedViewModeConsumed,
}) => {
    const { data: environment } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);
    const { data: organizationPolicies } = useGetOrganizationPolicies();
    const { value: systemPortsExcluded } = useSystemPortsExcludedFromPolicy();
    const [viewMode, setViewMode] = useEnvironmentViewMode(environment);
    const vscodeIpcRef = useRef<VSCodeIPC | null>(null);
    const { value: isReviewCommentsEnabled } = useFlagReviewCommentsEnabled();

    // Notify parent of current view mode
    useEffect(() => {
        onCurrentViewMode?.(viewMode);
    }, [viewMode, onCurrentViewMode]);

    useEffect(() => {
        if (!requestedViewMode) return;
        setViewMode(requestedViewMode);
        onRequestedViewModeConsumed?.();
    }, [requestedViewMode, setViewMode, onRequestedViewModeConsumed]);

    const { availableEditors } = useGetEditorForEnv(environment?.id);
    const isVSCodeBrowserAllowed = useMemo(() => {
        const hasVSCodeBrowser = availableEditors?.some((editor) => editor.alias === "vscode-browser") ?? false;
        const isVSCodeContext = !!window.isWebview;
        const portSharingAllowed = !organizationPolicies?.portSharingDisabled || systemPortsExcluded;
        return hasVSCodeBrowser && !isVSCodeContext && portSharingAllowed;
    }, [availableEditors, organizationPolicies?.portSharingDisabled, systemPortsExcluded]);
    const vscodeBrowserId = useMemo(
        () => availableEditors?.find((editor) => editor.alias === "vscode-browser")?.id,
        [availableEditors],
    );
    const { data: editorUrl } = useEditorURLQuery({
        editorId: vscodeBrowserId,
        environment: environment,
    });

    const [readyToLoadVSCode, setReadyToLoadVSCode] = useState(false);

    useEffect(() => {
        // Always load VS Code when environment is available and VS Code is allowed
        if (environment && isVSCodeBrowserAllowed && !readyToLoadVSCode) {
            setReadyToLoadVSCode(true);
        }
    }, [environment, isVSCodeBrowserAllowed, readyToLoadVSCode]);

    useEffect(() => {
        if (viewMode === "vscode" && !isVSCodeBrowserAllowed) {
            setViewMode("details");
        }
    }, [viewMode, isVSCodeBrowserAllowed, setViewMode]);

    const _onViewModeChange = useCallback(
        (mode: EnvironmentViewMode) => {
            setViewMode(mode);
            if (onViewModeChange) {
                onViewModeChange(mode);
            }
        },
        [setViewMode, onViewModeChange],
    );

    useEffect(() => {
        if (!followMode) return;

        if (followMode.enabled) {
            setViewMode("vscode");
        }

        if (!vscodeIpcRef.current) return;
        if (followMode.enabled) {
            void vscodeIpcRef.current.enableFollowMode();
        } else {
            void vscodeIpcRef.current.disableFollowMode();
        }
    }, [followMode, setViewMode, vscodeIpcRef]);

    return (
        <div className="flex h-full max-h-full flex-col">
            {!window.isWebview && (
                <EnvironmentDetailsTopHeader
                    environment={environment}
                    viewMode={viewMode}
                    onViewModeChange={_onViewModeChange}
                    onToggleDetailsPanel={onToggleDetailsPanel}
                    onToggleConversationPanel={onToggleConversationPanel}
                    isConversationPanelOpen={isConversationPanelOpen}
                    isVSCodeBrowserAllowed={isVSCodeBrowserAllowed}
                />
            )}
            <EnvironmentBanner environment={environment} runner={runner} />
            {viewMode === "details" && (
                <ScrollArea orientation="vertical" className="mx-[2px] my-[2px] h-full gap-6 p-4 md:p-6">
                    <Suspense>
                        <EnvironmentDetailsLayoutOutlet
                            context={{
                                showingAgentConversation: showConversationPanel,
                            }}
                        />
                    </Suspense>
                </ScrollArea>
            )}

            {viewMode === "changes" && (
                <ScrollArea orientation="vertical" className="mx-[2px] my-[2px] flex h-full flex-col gap-6 p-4 md:p-6">
                    <EnvironmentChangesView
                        ref={changesViewRef}
                        environmentId={environmentId}
                        enableReviewComments={isReviewCommentsEnabled}
                        pendingSpecPreview={pendingSpecPreview}
                        onPendingSpecPreviewConsumed={onPendingSpecPreviewConsumed}
                    />
                </ScrollArea>
            )}

            <div
                className={cn(
                    "size-full grow flex-col border-b border-l-0 border-r border-t-0 border-border-base",
                    viewMode === "vscode" ? "flex" : "hidden",
                )}
                data-vscode-visible={viewMode === "vscode"}
            >
                {readyToLoadVSCode && (
                    <VSCodeBrowserView
                        editorUrl={editorUrl?.url}
                        className="grow border-none"
                        followMode={followMode}
                        visible={viewMode === "vscode"}
                    />
                )}
            </div>
        </div>
    );
};

const EnvironmentDetailsWithConversation: FC<{
    environmentConversation: ReturnType<typeof useEnvironmentConversation>["environmentConversation"];
    environmentId: string;
}> = ({ environmentConversation, environmentId }) => {
    // Simple rule: track if code view is expanded per environment
    // Expanded code view = conversation hidden, everything else = conversation visible
    const getCodeViewExpanded = useCallback(() => {
        const key = `environment-code-expanded:${environmentId}`;
        return localStorage.getItem(key) === "true";
    }, [environmentId]);

    const setCodeViewExpanded = useCallback(
        (expanded: boolean) => {
            const key = `environment-code-expanded:${environmentId}`;
            localStorage.setItem(key, String(expanded));
        },
        [environmentId],
    );

    const [isConversationPanelCollapsed, setIsConversationPanelCollapsed] = useState(false);
    const showConversationPanel = !isConversationPanelCollapsed;
    const [currentViewMode, setCurrentViewMode] = useState<EnvironmentViewMode>("details");

    const [fixedConversationPanelSize, setFixedConversationPanelSize] = useState(0);
    const conversationPanelRef = useRef<ImperativePanelHandle>(null);
    const changesViewRef = useRef<EnvironmentChangesViewRef>(null);
    const panelGroupId = useId();
    const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
    const panelRef = useRef<ImperativePanelHandle>(null);
    const [isDetailsPanelCollapsed, setIsDetailsPanelCollapsed] = useState(false);
    const updatePanelSizeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const followMode = useFollowMode();

    const conversationPanelSize = showConversationPanel
        ? { defaultSize: 70, minSize: 30 }
        : { defaultSize: 0, minSize: 0 };

    const { isMobileViewport: isMobile } = useIsMobileViewport();
    const { value: isMobileVscodeEnabled } = useFlagIsMobileVscodeEnabled();

    // The library doesn't support animating the collapse/expand transition of panels [1]
    // You achieve it using CSS transitions, however, that breaks drag resizing behavior/performance
    // so the workaround is to only add those transitions when not dragging.
    //
    // [1] https://github.com/bvaughn/react-resizable-panels/issues/310#issuecomment-1974779047
    const [isDragging, setIsDragging] = useState(false);

    useEffect(
        () => () => {
            if (updatePanelSizeTimeout.current) {
                clearTimeout(updatePanelSizeTimeout.current);
            }
        },
        [],
    );

    useEffect(() => {
        if (!followMode) return;

        if (followMode.enabled) {
            panelRef.current?.expand();
        }
    }, [followMode, panelRef]);

    // Ensure conversation is always visible when in details or changes mode
    useEffect(() => {
        if ((currentViewMode === "details" || currentViewMode === "changes") && isConversationPanelCollapsed) {
            if (conversationPanelRef.current?.isCollapsed()) {
                conversationPanelRef.current.expand();
            }
            setIsConversationPanelCollapsed(false);
        }
    }, [currentViewMode, isConversationPanelCollapsed]);

    const handleToggleDetailsPanel = useCallback(() => {
        if (!panelRef.current) {
            return;
        }

        setFixedConversationPanelSize(0);

        if (panelRef.current.isCollapsed()) {
            panelRef.current.expand();
        } else {
            panelRef.current.collapse();
            followMode?.setEnabled(false);
        }
    }, [panelRef, followMode]);

    const handleToggleConversationPanel = useCallback(() => {
        if (!conversationPanelRef.current) {
            return;
        }

        const willBeExpanded = conversationPanelRef.current.isCollapsed();

        if (willBeExpanded) {
            // Minimizing code view - show conversation
            conversationPanelRef.current.expand();
            setIsConversationPanelCollapsed(false);
            setCodeViewExpanded(false);
        } else {
            // Expanding code view - hide conversation
            conversationPanelRef.current.collapse();
            setIsConversationPanelCollapsed(true);
            setCodeViewExpanded(true);
        }
    }, [setCodeViewExpanded]);

    const onViewModeChange = useCallback(
        (mode: EnvironmentViewMode) => {
            setCurrentViewMode(mode);
            switch (mode) {
                case "details":
                case "changes": {
                    followMode?.setEnabled(false);
                    // Switching to details/changes view = minimizing code view = show conversation
                    if (conversationPanelRef.current?.isCollapsed()) {
                        conversationPanelRef.current.expand();
                        setIsConversationPanelCollapsed(false);
                    }
                    setCodeViewExpanded(false);
                    break;
                }
                case "vscode": {
                    // Switching to code view - restore per-environment expanded state
                    const isExpanded = getCodeViewExpanded();
                    const isCurrentlyCollapsed = conversationPanelRef.current?.isCollapsed() ?? false;

                    if (isExpanded && !isCurrentlyCollapsed) {
                        // Should be expanded (conversation hidden)
                        conversationPanelRef.current?.collapse();
                        setIsConversationPanelCollapsed(true);
                    } else if (!isExpanded && isCurrentlyCollapsed) {
                        // Should not be expanded (conversation visible)
                        conversationPanelRef.current?.expand();
                        setIsConversationPanelCollapsed(false);
                    }
                    break;
                }
            }

            const id = conversationPanelRef.current?.getId();
            const conversationPanelElement = document.getElementById(id ?? "");
            if (mode === "vscode" && conversationPanelElement) {
                setFixedConversationPanelSize(conversationPanelElement.clientWidth);
                // wait for sidebar to finish animating out, then calculate update the panel proportion imperatively
                updatePanelSizeTimeout.current = setTimeout(() => {
                    const groupElement = getPanelGroupElement(panelGroupId);
                    const id = conversationPanelRef.current?.getId();
                    const conversationPanelElement = getPanelElement(id ?? "");
                    if (groupElement && conversationPanelElement && panelGroupRef.current) {
                        const proportionalSize =
                            (conversationPanelElement.clientWidth / groupElement.clientWidth) * 100;
                        panelGroupRef.current.setLayout([proportionalSize, 100 - proportionalSize]);
                    }
                }, 250);
            }
        },
        [followMode, getCodeViewExpanded, setCodeViewExpanded, panelGroupId],
    );

    const [pendingSpecPreview, setPendingSpecPreview] = useState<string | null>(null);
    const clearPendingSpecPreview = useCallback(() => setPendingSpecPreview(null), []);

    const [requestedViewMode, setRequestedViewMode] = useState<EnvironmentViewMode | null>(null);
    const clearRequestedViewMode = useCallback(() => setRequestedViewMode(null), []);

    const handleBuildButtonAppear = useCallback(
        (specFilePath: string | null) => {
            if (isMobile) return;

            if (panelRef.current?.isCollapsed()) {
                panelRef.current.expand();
            }

            onViewModeChange("changes");
            setRequestedViewMode("changes");

            if (specFilePath) {
                setPendingSpecPreview(specFilePath);
            }
        },
        [isMobile, onViewModeChange, panelRef],
    );

    const onDragging = (isDragging: boolean) => {
        setIsDragging(isDragging);
        if (fixedConversationPanelSize) {
            setFixedConversationPanelSize(0);
        }
    };

    // Only auto-collapse details panel on mobile when mobile VSCode is disabled
    useEffect(() => {
        if (isMobile && !isMobileVscodeEnabled) {
            setIsDetailsPanelCollapsed(true);
        }
    }, [isMobile, isMobileVscodeEnabled]);

    return (
        <MainContent noPadding>
            {isMobile ? (
                isDetailsPanelCollapsed ? (
                    <EnvironmentAgentExecutionConversationPanel
                        environmentConversation={environmentConversation}
                        environmentId={environmentId}
                        isDetailsPanelOpen={false}
                        onToggleDetailsPanel={() => {
                            setIsDetailsPanelCollapsed(false);
                        }}
                        followMode={followMode}
                        onBuildButtonAppear={handleBuildButtonAppear}
                    />
                ) : (
                    <EnvironmentDetailsPanel
                        environmentId={environmentId}
                        showConversationPanel={showConversationPanel}
                        onToggleDetailsPanel={() => {
                            setIsDetailsPanelCollapsed(true);
                        }}
                        onToggleConversationPanel={() => {
                            setIsDetailsPanelCollapsed(false);
                        }}
                        isConversationPanelOpen={!isDetailsPanelCollapsed}
                        followMode={followMode}
                        onCurrentViewMode={setCurrentViewMode}
                        changesViewRef={changesViewRef}
                        pendingSpecPreview={pendingSpecPreview}
                        onPendingSpecPreviewConsumed={clearPendingSpecPreview}
                        requestedViewMode={requestedViewMode}
                        onRequestedViewModeConsumed={clearRequestedViewMode}
                    />
                )
            ) : (
                <ResizablePanelGroup
                    id={panelGroupId}
                    ref={panelGroupRef}
                    direction="horizontal"
                    className="flex"
                    autoSaveId="environment-details-layout"
                >
                    <ResizablePanel
                        ref={conversationPanelRef}
                        defaultSize={conversationPanelSize.defaultSize}
                        minSize={conversationPanelSize.minSize}
                        collapsedSize={0}
                        collapsible={currentViewMode === "vscode"}
                        order={1}
                        className={cn({
                            // See definition of isDragging for why we're doing this selectively
                            "transition-[flex-basis,flex-grow,flex-shrink] duration-100 ease-in-out": !isDragging,
                        })}
                        style={
                            fixedConversationPanelSize
                                ? {
                                      maxWidth: `${fixedConversationPanelSize}px`,
                                  }
                                : {}
                        }
                        onCollapse={() => setIsConversationPanelCollapsed(true)}
                        onExpand={() => setIsConversationPanelCollapsed(false)}
                        id="conversation-panel"
                    >
                        <EnvironmentAgentExecutionConversationPanel
                            environmentConversation={environmentConversation}
                            environmentId={environmentId}
                            isDetailsPanelOpen={!isDetailsPanelCollapsed}
                            onToggleDetailsPanel={handleToggleDetailsPanel}
                            followMode={followMode}
                            onBuildButtonAppear={handleBuildButtonAppear}
                        />
                    </ResizablePanel>
                    <ResizableHandle
                        disabled={isConversationPanelCollapsed || isDetailsPanelCollapsed}
                        onDragging={onDragging}
                    />
                    <ResizablePanel
                        defaultSize={100 - conversationPanelSize.defaultSize}
                        minSize={30}
                        collapsedSize={0}
                        ref={panelRef}
                        collapsible
                        className={cn({
                            // See definition of isDragging for why we're doing this selectively
                            "transition-[flex-basis,flex-grow,flex-shrink] duration-100 ease-in-out": !isDragging,
                        })}
                        onCollapse={() => setIsDetailsPanelCollapsed(true)}
                        onExpand={() => setIsDetailsPanelCollapsed(false)}
                        order={2}
                        id="environment-details"
                    >
                        <EnvironmentDetailsPanel
                            environmentId={environmentId}
                            showConversationPanel={showConversationPanel}
                            onViewModeChange={onViewModeChange}
                            onToggleConversationPanel={handleToggleConversationPanel}
                            onToggleDetailsPanel={handleToggleDetailsPanel}
                            isConversationPanelOpen={!isConversationPanelCollapsed}
                            followMode={followMode}
                            onCurrentViewMode={setCurrentViewMode}
                            changesViewRef={changesViewRef}
                            pendingSpecPreview={pendingSpecPreview}
                            onPendingSpecPreviewConsumed={clearPendingSpecPreview}
                            requestedViewMode={requestedViewMode}
                            onRequestedViewModeConsumed={clearRequestedViewMode}
                        />
                    </ResizablePanel>
                </ResizablePanelGroup>
            )}
        </MainContent>
    );
};

/**
 * Outlet wrapper for EnvironmentDetailsOutletContext to ensure type-safety if we update the context type.
 */
const EnvironmentDetailsLayoutOutlet: FC<{ context: EnvironmentDetailsOutletContext }> = ({ context }) => {
    return <Outlet context={context} />;
};
